import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | ok | error

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("error"); return; }
    api.post("/auth/verify-email", { token })
      .then(async () => { setState("ok"); try { await refresh(); } catch {} })
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
        {state === "loading" && (<><Loader2 className="h-10 w-10 text-blue-500 animate-spin mx-auto" /><p className="mt-4 text-slate-500">Verifying your email…</p></>)}
        {state === "ok" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center"><CheckCircle2 className="h-7 w-7 text-white" /></div>
            <h1 className="font-heading text-2xl font-bold mt-4 text-slate-900">Email verified!</h1>
            <p className="text-slate-500 mt-2">Your account is now active. Let's finish setting up your AI receptionist.</p>
            <Button data-testid="continue-onboarding" onClick={() => navigate("/onboarding")} className="mt-6 bg-blue-600 hover:bg-blue-700 w-full">Continue setup</Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-red-500 flex items-center justify-center"><XCircle className="h-7 w-7 text-white" /></div>
            <h1 className="font-heading text-2xl font-bold mt-4 text-slate-900">Link expired or invalid</h1>
            <p className="text-slate-500 mt-2">This verification link is invalid or has already been used. Sign in to request a fresh one.</p>
            <Link to="/login"><Button className="mt-6 w-full" variant="outline"><MailCheck className="h-4 w-4 mr-2" /> Go to sign in</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
